import { ReactNode } from 'react';
import { useAuthSessionRestoreContext } from 'context';
import { useIsUserLoggedIn } from 'pages/Auth/hooks';
import AblyContextProvider from './AblyProvider';

/**
 * Mounts a single shared Ably Realtime client for the SPA session while the user is logged in.
 *
 * We also mount when a JWT exists but the user object is not hydrated yet; otherwise routes can
 * render Main (private + token) before `useIsUserLoggedIn` is true and hooks like `useAbly` would
 * run without an AblyProvider. `accessToken` is read from the shared `AuthSessionRestoreContext`
 * (provided by `AuthSessionRestoreProvider`, mounted as this component's parent in App.tsx) rather
 * than a local storage read, so this check and the route guards in CustomRoute/Routes see the exact
 * same value and update in the same React commit — a plain storage read here was re-evaluated only
 * on this component's own re-renders, which could lag behind a sibling route flipping its token state.
 */
const AppAblyProvider = ({ children }: { children: ReactNode }) => {
  const isLoggedIn = useIsUserLoggedIn();
  const { accessToken } = useAuthSessionRestoreContext();
  const shouldMountAbly = isLoggedIn || Boolean(accessToken);

  if (!shouldMountAbly) {
    return <>{children}</>;
  }

  return <AblyContextProvider>{children}</AblyContextProvider>;
};

export default AppAblyProvider;
